/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useReducer, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import {
  Button,
  Checkbox,
  Description,
  FlexContainer,
  Input,
  Label,
  Sidebar,
  Tag,
  theme,
} from "@nordcloud/gnui";
import {
  Role,
  useCreateApiKeyPermissionsMutation,
  useUpdateApiKeyPermissionsMutation,
} from "~/generated/graphql";
import { Fieldset } from "~/components";
import { ERROR_TEXT } from "~/constants";
import { showError } from "~/services/toast";
import { ApiKeySchema, UpdateApiKeyInput } from "../validators/ApiKeys";
import { ApiKeySuccessModal } from "./ApiKeySuccessModal";
import { ApplicationsSidebar } from "./ApplicationsSidebar";
import {
  ACTION,
  initialSecretState,
  initialState,
  reducer,
  SECRET_ACTION,
  secretReducer,
} from "./reducers";
import { RoleRadio } from "./RoleRadio";
import { text } from "./text";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  refetch: () => void;
  edit?: boolean;
  editItem?: UpdateApiKeyInput | null;
};

// TODO: Reduce amount of code
export function AddApiKeySidebar({
  isOpen,
  setIsOpen,
  refetch,
  edit,
  editItem,
}: Props) {
  const [secretModalState, secretDispatch] = useReducer(
    secretReducer,
    initialSecretState
  );
  const [state, dispatch] = useReducer(reducer, initialState);

  const [createApiKey] = useCreateApiKeyPermissionsMutation();
  const [updateApiKey] = useUpdateApiKeyPermissionsMutation();

  useEffect(() => {
    if (isOpen) {
      dispatch({
        type: ACTION.SET,
        payload: {
          description: editItem?.input?.description ?? "",
          role: editItem?.input?.role ?? Role.ReadOnly,
          applications: editItem?.input?.applications ?? [],
        },
      });
    }
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ApiKeySchema),
  });

  const onAddApiKey = async () => {
    const response = await createApiKey({
      variables: {
        input: {
          description: state.description,
          role: state.role,
          applications: state.applications,
          expirationDate: state.expirationDate,
        },
      },
    });
    return {
      keyId: response?.data?.createAccessKey?.id ?? "",
      keySecret: response?.data?.createAccessKey?.secret ?? "",
    };
  };

  const onSubmit = async () => {
    try {
      if (edit) {
        if (editItem?.id == null) {
          throw new Error("API Key ID missing");
        }
        await updateApiKey({
          variables: {
            id: editItem.id,
            input: {
              description: state.description,
              role: state.role,
              applications:
                state.role === Role.TeamLead ? state.applications : [],
            },
          },
        });
      } else {
        const apiKeyData = await onAddApiKey();
        secretDispatch({
          type: SECRET_ACTION.OPEN,
          payload: apiKeyData,
        });
      }
      refetch();
      setIsOpen(false);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const setRole = (role: Role) => {
    dispatch({ type: ACTION.ROLE, role });
  };

  return (
    <>
      <ApiKeySuccessModal
        isOpen={secretModalState.open}
        data={secretModalState}
        onClose={() => secretDispatch({ type: SECRET_ACTION.CLOSE })}
      />
      <Sidebar
        isOpen={isOpen}
        title={edit ? "Edit API Key" : "Add New API Key"}
        footer={
          <FlexContainer justifyContent="flex-end" css={{ width: "100%" }}>
            <Button
              m={theme.spacing.spacing04}
              ml="0"
              type="submit"
              form="addAPIKeyForm"
              icon="plus"
              data-testid="apikey-sidebar-submit"
            >
              {edit ? "Edit API Key" : "Add New API Key"}
            </Button>
          </FlexContainer>
        }
        onClick={() => setIsOpen(false)}
      >
        <form id="addAPIKeyForm" onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <Label required name="API Key Description" />
            <Input
              type="text"
              {...register("description", { required: true })}
              value={state.description}
              placeholder="Description"
              onChange={(event) =>
                dispatch({
                  type: ACTION.DESCRIPTION,
                  description: event.target.value,
                })
              }
            />
            {errors.description && (
              <Description>Description is required.</Description>
            )}
          </Fieldset>
          {edit || (
            <Fieldset>
              <Checkbox
                labelText="Expiration Date"
                checked={state.expires}
                onChange={() =>
                  dispatch({
                    type: ACTION.EXPIRATION,
                    expirationDate: dayjs().add(7, "day").format("YYYY-MM-DD"),
                    expires: !state.expires,
                  })
                }
              />
              <Input
                disabled={!state.expires}
                min={dayjs().add(1, "day").format("YYYY-MM-DD")}
                pattern="\d{4}-\d{2}-\d{2}"
                type="date"
                {...register("expiration", { required: false })}
                value={state.expirationDate}
                placeholder="Expiration date"
                onChange={(event) =>
                  dispatch({
                    type: ACTION.EXPIRATION,
                    expirationDate: event.target.value,
                    expires: true,
                  })
                }
              />
            </Fieldset>
          )}
          <Fieldset>
            <Label required name="Select Role" />
            <div className="radio-group">
              <RoleRadio
                register={register}
                setRole={setRole}
                role={Role.Admin}
                selectedRole={state.role}
                label="Full access"
                description={text.forms.accessDescription.full}
              />
              <RoleRadio
                register={register}
                setRole={setRole}
                role={Role.ReadOnly}
                selectedRole={state.role}
                label="Read-only"
                description={text.forms.accessDescription.readonly}
              />
              <RoleRadio
                register={register}
                setRole={setRole}
                role={Role.TeamLead}
                selectedRole={state.role}
                label="Restricted access"
                description={text.forms.accessDescription.restricted}
              />
            </div>
          </Fieldset>
          {state.showApps && (
            <Fieldset>
              <Label name="Permissions" />
              <FlexContainer justifyContent="space-between" alignItems="center">
                <div>{`${state.applications.length} applications selected`}</div>
                <Tag
                  icon="plus"
                  text="Select"
                  onClick={() =>
                    dispatch({ type: ACTION.APP_SELECTION, show: true })
                  }
                />
              </FlexContainer>
            </Fieldset>
          )}
        </form>
      </Sidebar>
      <ApplicationsSidebar dispatch={dispatch} state={state} />
    </>
  );
}
